import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { BreakpointValue } from "@store/Framework/Common/Utilities/breakpointValues";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionThemeValue,
} from "@store/Framework/Common/Utilities/dataOptions";
import { LiteralUnion } from "@store/Framework/Types/Common";
import { StyledTheme } from "@store/Framework/Types/StyledTheme";
import isArray from "lodash/isArray";
import { useMemo } from "react";
import { useTheme } from "styled-components";

export type VerticalGapValue = BreakpointValue[];
export type VerticalGapOption = DataOption<VerticalGapValue | string>;
export interface VerticalGapOptionMapper {
    none: VerticalGapOption;
    block: VerticalGapOption;
    narrow: VerticalGapOption;
    content: VerticalGapOption;
}
export type VerticalGapOptions = DataOptions<VerticalGapOption> & VerticalGapOptionMapper;
export type VerticalGap = LiteralUnion<keyof VerticalGapOptionMapper>;

export const verticalGapOptions: VerticalGapOptions = {
    none: { id: "None", displayName: "None", value: [] },
    block: { id: "Block", displayName: "Standard", value: "design.layout.spacing.content" },
    narrow: { id: "Narrow", displayName: "Narrow", value: "design.layout.spacing.narrowContent" },
    content: { id: "Content", displayName: "Content", value: "design.typography.spacing.base" },
};

export type VerticalGapFieldValue = VerticalGap | undefined;
export interface VerticalGapFieldOptions {
    isChildDefinition?: boolean;
}

export const getVerticalGapFieldData = (
    value: VerticalGapFieldValue,
    theme: StyledTheme,
    options?: VerticalGapFieldOptions,
) => {
    return value ? getDataOptionThemeValue<VerticalGapValue>(verticalGapOptions, value, theme) : undefined;
};

export const getVerticalGapFieldDefinition = (name: string, options?: VerticalGapFieldOptions) => {
    const { isChildDefinition } = options || {};

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(verticalGapOptions),
        name,
        displayName: "Stacked column spacing",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        tooltip:
            'Determines spacing between the columns when they stack on top of each other for smaller devices (Default: Uses the "Gap" configuration)',
        tooltipPosition: "left",
    };

    return definition;
};

const getVerticalGapField = (name: string, options?: VerticalGapFieldOptions) => {
    const parse = (value: VerticalGapFieldValue, theme: StyledTheme) => {
        const data = getVerticalGapFieldData(value, theme);
        return data ? (isArray(data) ? data : [data]) : undefined;
    };

    return {
        definition: getVerticalGapFieldDefinition(name, options),
        parse,
        useParse: (value: VerticalGapFieldValue) => {
            const theme = useTheme();

            return useMemo(() => parse(value, theme), [value, theme]);
        },
    };
};

export default getVerticalGapField;
